import styled from '@emotion/styled';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import {
    $createRangeSelection,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $nodesOfType,
    $setSelection,
    type TextNode,
} from 'lexical';
import type { FunctionComponent, MouseEvent, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { PulsatingCircle } from '~/ui/core/icons';
import { $unwrapCommentMarkNode, CommentMarkNode } from '~/ui/core/rich-editor/CommentMarkNode';
import { $wrapTextRangeInCommentMark } from '~/ui/core/rich-editor/comment-mark-selection';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import { useActiveThreadStore } from '../../comments/ActiveThreadStoreProvider';
import type { PendingCommentTarget } from '../../comments/active-thread-store';
import {
    ANCHOR_ITEM_ID_ATTRIBUTE,
    COMMENT_ID_ATTRIBUTE,
    commentMarkSelector,
    descriptionSectionAnchorAttrs,
} from '../../comments/anchor/grammar';
import { useOpenThread } from '../../comments/anchor/use-commentable-anchor';
import { CommentableDescriptionSection, PlainDescriptionSection } from '../../comments/CommentableDescriptionSection';
import { CommentSelectionPlugin } from '../../comments/CommentSelectionPlugin';
import type { CommentMarkRange } from '../../comments/comment-classification';
import { isCommentsScopeCurrent, useOptionalCommentsScope } from '../../comments/comments-scope-context';
import { stripCommentMarks } from '../../comments/strip-comment-marks';
import { reportRecoveredRichEditorError } from '../../data-dog';
import { RichEditorToolbarWithTranscribe } from './RichEditorToolbarWithTranscribe';
import type { ProfileForm } from './types';

export type EditableDescriptionSectionProps = {
    readonly activeLanguageIndex: number;
    readonly commentable: boolean;
    readonly control: Control<ProfileForm>;
    readonly index: number;
    readonly isGenerating: boolean;
    readonly commentMarkedHtml?: string;
    readonly itemId?: string;
    readonly onUserInput?: () => void;
    readonly sectionKey?: string;
    readonly sectionTitle: string;
};

const EditorContainer = styled.div`
    &:focus,
    &:focus-visible {
        border: none;
        box-shadow: none;
        outline: none;
    }

    position: relative;
`;

const TitleText = styled.div`
    background: none;
    border: none;
    color: var(--color-text-primary);
    font-family: ${({ theme }) => theme.typography.body.S.medium.fontFamily};
    font-size: ${({ theme }) => theme.typography.body.S.medium.fontSize};
    font-style: normal;
    font-weight: ${({ theme }) => theme.typography.body.S.medium.fontWeight};
    line-height: ${({ theme }) => theme.typography.body.S.medium.lineHeight};
    outline: none;
    text-transform: capitalize;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TitleWithLoaderContainer = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.s};
    justify-content: flex-start;
    margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const EMPTY_COMMENT_MARK_RANGES: readonly CommentMarkRange[] = [];

const resolveEditorValue = (
    value: unknown,
    commentMarkedHtml: string | undefined,
    hasEdited = false,
): string | undefined => {
    const strippedValue = is.string(value) ? stripCommentMarks(value) : undefined;

    if (is.string(commentMarkedHtml) && !hasEdited) {
        const strippedMarkedHtml = stripCommentMarks(commentMarkedHtml);

        return is.undefined(strippedValue) || strippedValue === strippedMarkedHtml ? commentMarkedHtml : strippedValue;
    }

    return strippedValue;
};

const sameCommentRanges = (first: readonly CommentMarkRange[], second: readonly CommentMarkRange[]): boolean =>
    first.length === second.length &&
    first.every((range) =>
        second.some(
            (other) => other.commentId === range.commentId && other.start === range.start && other.end === range.end,
        ),
    );

const $hasReconcileWork = (
    validCommentIds: ReadonlySet<string>,
    resolvedRanges: readonly CommentMarkRange[],
    checkPositions: boolean,
): boolean => {
    const marks = $nodesOfType(CommentMarkNode);
    if (marks.some((mark) => !validCommentIds.has(mark.getCommentId()))) {
        return true;
    }
    const presentIds = new Set(marks.map((mark) => mark.getCommentId()));
    if (resolvedRanges.some((range) => !presentIds.has(range.commentId))) {
        return true;
    }
    if (!checkPositions) {
        return false;
    }
    const textNodes = $getRoot().getAllTextNodes();
    return resolvedRanges.some((range) => {
        let offset = 0;
        let start: number | undefined;
        let end: number | undefined;
        for (const node of textNodes) {
            const nodeEnd = offset + node.getTextContentSize();
            if (marks.some((mark) => mark.getCommentId() === range.commentId && mark.isParentOf(node))) {
                start ??= offset;
                end = nodeEnd;
            }
            offset = nodeEnd;
        }
        return start !== range.start || end !== range.end;
    });
};

const $selectionTextOffset = (key: string, pointOffset: number): number | null => {
    let offset = 0;
    for (const node of $getRoot().getAllTextNodes()) {
        if (node.getKey() === key) {
            return offset + pointOffset;
        }
        offset += node.getTextContentSize();
    }
    return null;
};

type SavedTextPoint = { readonly offset: number; readonly preferNext: boolean };
type SavedTextSelection = {
    readonly anchor: SavedTextPoint;
    readonly focus: SavedTextPoint;
    readonly format: number;
    readonly style: string;
};

const $textPointAtOffset = ({ offset: target, preferNext }: SavedTextPoint) => {
    let offset = 0;
    let lastNode: TextNode | undefined;
    for (const node of $getRoot().getAllTextNodes()) {
        const end = offset + node.getTextContentSize();
        if (target < end || (target === end && !preferNext)) {
            return { key: node.getKey(), offset: target - offset };
        }
        offset = end;
        lastNode = node;
    }
    return lastNode === undefined ? null : { key: lastNode.getKey(), offset: lastNode.getTextContentSize() };
};

const $restoreTextSelection = (saved: SavedTextSelection): void => {
    const anchor = $textPointAtOffset(saved.anchor);
    const focus = $textPointAtOffset(saved.focus);
    if (is.null(anchor) || is.null(focus)) {
        return;
    }
    const selection = $createRangeSelection();
    selection.anchor.set(anchor.key, anchor.offset, 'text');
    selection.focus.set(focus.key, focus.offset, 'text');
    selection.setFormat(saved.format);
    selection.setStyle(saved.style);
    $setSelection(selection);
};

const $saveTextSelection = (): SavedTextSelection | null => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.anchor.type !== 'text' || selection.focus.type !== 'text') {
        return null;
    }
    const anchor = $selectionTextOffset(selection.anchor.key, selection.anchor.offset);
    const focus = $selectionTextOffset(selection.focus.key, selection.focus.offset);
    return is.null(anchor) || is.null(focus)
        ? null
        : {
              anchor: { offset: anchor, preferNext: selection.anchor.offset === 0 },
              focus: { offset: focus, preferNext: selection.focus.offset === 0 },
              format: selection.format,
              style: selection.style,
          };
};

const $reconcileCommentMarks = (
    validCommentIds: ReadonlySet<string>,
    resolvedRanges: readonly CommentMarkRange[],
): void => {
    const savedSelection = $getSelection()?.clone() ?? null;
    const savedOffsets = $saveTextSelection();
    const resolvedCommentIds = new Set(resolvedRanges.map((range) => range.commentId));

    const presentIds = new Set<string>();
    for (const mark of $nodesOfType(CommentMarkNode)) {
        if (validCommentIds.has(mark.getCommentId()) && !resolvedCommentIds.has(mark.getCommentId())) {
            presentIds.add(mark.getCommentId());
        } else {
            $unwrapCommentMarkNode(mark);
        }
    }

    const additions: Array<{ readonly end: number; readonly id: string; readonly start: number }> = [];

    for (const range of resolvedRanges) {
        if (presentIds.has(range.commentId)) {
            continue;
        }
        additions.push({ end: range.end, id: range.commentId, start: range.start });
    }

    additions.sort((first, second) => first.start - second.start || second.end - first.end);

    for (const addition of additions) {
        $wrapTextRangeInCommentMark(addition.start, addition.end, addition.id);
    }

    if (!is.null(savedOffsets)) {
        $restoreTextSelection(savedOffsets);
    } else {
        $setSelection(savedSelection);
    }
};

const CommentMarkReconcilePlugin: FunctionComponent<{
    readonly editorValue: string | undefined;
    readonly resolvedRanges: readonly CommentMarkRange[];
    readonly validCommentIds: ReadonlySet<string> | null;
}> = ({ editorValue, resolvedRanges, validCommentIds }) => {
    const [editor] = useLexicalComposerContext();
    const previousRanges = useRef<readonly CommentMarkRange[]>([]);
    useEffect(() => {
        if (is.null(validCommentIds) || is.undefined(editorValue)) {
            return;
        }

        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) {
                return;
            }
            const checkPositions = !sameCommentRanges(previousRanges.current, resolvedRanges);
            const expectedText = new DOMParser().parseFromString(editorValue, 'text/html').body.textContent ?? '';
            const currentText = editor.getEditorState().read(() =>
                $getRoot()
                    .getAllTextNodes()
                    .map((node) => node.getTextContent())
                    .join(''),
            );
            if (editor.getRootElement()?.contains(document.activeElement) && currentText !== expectedText) {
                return;
            }
            previousRanges.current = resolvedRanges;
            const hasWork = editor
                .getEditorState()
                .read(() => $hasReconcileWork(validCommentIds, resolvedRanges, checkPositions));
            if (!hasWork) {
                return;
            }
            editor.update(() => $reconcileCommentMarks(validCommentIds, resolvedRanges), {
                tag: 'external-sync',
            });
        });

        return () => {
            cancelled = true;
        };
    }, [editor, editorValue, resolvedRanges, validCommentIds]);

    return null;
};

const buildValidCommentIds = (
    resolvedRanges: readonly CommentMarkRange[] | null,
    pendingTarget: PendingCommentTarget | null,
    sectionKey: string,
): ReadonlySet<string> | null => {
    if (is.null(resolvedRanges)) {
        return null;
    }

    const ids = new Set(resolvedRanges.map((range) => range.commentId));
    if (
        !is.null(pendingTarget) &&
        pendingTarget.target.kind === 'description-section' &&
        pendingTarget.target.sectionId === sectionKey
    ) {
        ids.add(pendingTarget.commentId);
    }
    return ids;
};

const CommentMarkReconcilePluginFromComments: FunctionComponent<{
    readonly editorValue: string | undefined;
    readonly sectionKey: string;
}> = ({ editorValue, sectionKey }) => {
    const scope = useOptionalCommentsScope();
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);
    const resolvedRanges = useActiveThreadStore((state) => {
        if (is.null(state.commentClassification)) {
            return null;
        }
        if (!isCommentsScopeCurrent(state.commentClassificationScope, scope)) {
            return EMPTY_COMMENT_MARK_RANGES;
        }
        return (
            state.commentClassification.sectionMarkRanges.find((section) => section.sectionId === sectionKey)?.ranges ??
            EMPTY_COMMENT_MARK_RANGES
        );
    });
    const validCommentIds = useMemo<ReadonlySet<string> | null>(
        () => buildValidCommentIds(resolvedRanges, pendingTarget, sectionKey),
        [pendingTarget, resolvedRanges, sectionKey],
    );

    return (
        <CommentMarkReconcilePlugin
            editorValue={editorValue}
            resolvedRanges={resolvedRanges ?? []}
            validCommentIds={validCommentIds}
        />
    );
};

const useStructuredSectionField = ({
    activeLanguageIndex,
    commentMarkedHtml,
    control,
    index,
}: {
    readonly activeLanguageIndex: number;
    readonly commentMarkedHtml: string | undefined;
    readonly control: Control<ProfileForm>;
    readonly index: number;
}): { readonly editorValue: string | undefined; readonly handleEditorChange: (html: string) => void } => {
    const {
        field: { onChange, value },
    } = useController({ control, name: `translations.${activeLanguageIndex}.structured.${index}.value` });

    const hasEdited = useRef(false);
    const lastEmittedHtml = useRef<string>(undefined);
    const resolvedEditorValue = resolveEditorValue(value, commentMarkedHtml, hasEdited.current);
    const [editorValue, setEditorValue] = useState(resolvedEditorValue);

    useEffect(() => {
        if (is.string(value) && stripCommentMarks(lastEmittedHtml.current ?? '') === stripCommentMarks(value)) {
            return;
        }
        setEditorValue((currentEditorValue) =>
            currentEditorValue === resolvedEditorValue ? currentEditorValue : resolvedEditorValue,
        );
    }, [resolvedEditorValue, value]);

    const handleEditorChange = useCallback(
        (html: string) => {
            hasEdited.current = true;
            lastEmittedHtml.current = html;
            const strippedHtml = stripCommentMarks(html);
            setEditorValue(html);
            onChange(strippedHtml);
        },
        [onChange],
    );

    return { editorValue, handleEditorChange };
};

type StructuredSectionEditorProps = {
    readonly activeLanguageIndex: number;
    readonly children?: ReactNode;
    readonly editorValue: string | undefined;
    readonly index: number;
    readonly isGenerating: boolean;
    readonly itemId?: string;
    readonly onChange: (html: string) => void;
    readonly onContainerClick?: (event: MouseEvent<HTMLDivElement>) => void;
    readonly onUserInput?: () => void;
    readonly sectionKey?: string;
    readonly sectionTitle: string;
};

const StructuredSectionEditor: FunctionComponent<StructuredSectionEditorProps> = ({
    activeLanguageIndex,
    children,
    editorValue,
    index,
    isGenerating,
    itemId,
    onChange,
    onContainerClick,
    onUserInput,
    sectionKey,
    sectionTitle,
}) => {
    const handleEditorError = useCallback(
        (error: Error) => {
            reportRecoveredRichEditorError({ activeLanguageIndex, index, sectionTitle }, error);
        },
        [activeLanguageIndex, index, sectionTitle],
    );

    const editorWrapperClassName = clsx(
        'overflow-hidden rounded-sm border border-border-control',
        '[&_.editor-comment-mark]:rounded-xxs [&_.editor-comment-mark]:box-decoration-clone [&_.editor-comment-mark]:text-inherit',
        '[&_.editor-comment-mark]:bg-orange-100',
        'dark:[&_.editor-comment-mark]:bg-comment-highlight-surface!',
        '[&_.editor-comment-mark_.editor-comment-mark]:bg-orange-200',
        'dark:[&_.editor-comment-mark_.editor-comment-mark]:bg-comment-highlight-nested-surface!',
        '[&_.editor-comment-mark:hover]:bg-orange-300',
        'dark:[&_.editor-comment-mark:hover]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark.is-active]:bg-orange-300',
        'dark:[&_.editor-comment-mark.is-active]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark:hover_.editor-comment-mark]:bg-orange-300',
        'dark:[&_.editor-comment-mark:hover_.editor-comment-mark]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark.is-active_.editor-comment-mark]:bg-orange-300',
        'dark:[&_.editor-comment-mark.is-active_.editor-comment-mark]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark_.editor-comment-mark:hover]:bg-orange-300',
        'dark:[&_.editor-comment-mark_.editor-comment-mark:hover]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark_.editor-comment-mark.is-active]:bg-orange-300',
        'dark:[&_.editor-comment-mark_.editor-comment-mark.is-active]:bg-comment-highlight-active-surface!',
        '[&_.editor-comment-mark.is-pending]:bg-orange-300',
        'dark:[&_.editor-comment-mark.is-pending]:bg-comment-highlight-active-surface!',
    );

    return (
        <EditorContainer
            {...(is.string(itemId) && is.string(sectionKey)
                ? descriptionSectionAnchorAttrs({ itemId })
                : { [ANCHOR_ITEM_ID_ATTRIBUTE]: itemId })}
            data-section-key={sectionKey}
            data-testid="editable-description-section-container"
            onClick={onContainerClick}
        >
            <TitleWithLoaderContainer>
                <TitleText>{sectionTitle}</TitleText>
                {isGenerating && <PulsatingCircle />}
            </TitleWithLoaderContainer>
            <div className="relative">
                <div className={editorWrapperClassName}>
                    <RichEditor
                        ariaLabel={sectionTitle}
                        toolbar={<RichEditorToolbarWithTranscribe />}
                        value={editorValue}
                        onChange={onChange}
                        onError={handleEditorError}
                        onUserInput={onUserInput}
                    >
                        {children}
                    </RichEditor>
                </div>
            </div>
        </EditorContainer>
    );
};

type PlainEditableDescriptionSectionProps = Pick<
    EditableDescriptionSectionProps,
    'activeLanguageIndex' | 'control' | 'index' | 'isGenerating' | 'onUserInput' | 'sectionTitle'
>;

const PlainEditableDescriptionSection: FunctionComponent<PlainEditableDescriptionSectionProps> = ({
    activeLanguageIndex,
    control,
    index,
    isGenerating,
    onUserInput,
    sectionTitle,
}) => {
    const { editorValue, handleEditorChange } = useStructuredSectionField({
        activeLanguageIndex,
        commentMarkedHtml: undefined,
        control,
        index,
    });

    return (
        <StructuredSectionEditor
            activeLanguageIndex={activeLanguageIndex}
            editorValue={editorValue}
            index={index}
            isGenerating={isGenerating}
            sectionTitle={sectionTitle}
            onChange={handleEditorChange}
            onUserInput={onUserInput}
        />
    );
};

const CommentableEditableDescriptionSection: FunctionComponent<EditableDescriptionSectionProps> = ({
    activeLanguageIndex,
    commentMarkedHtml,
    control,
    index,
    isGenerating,
    itemId,
    onUserInput,
    sectionKey,
    sectionTitle,
}) => {
    const { editorValue, handleEditorChange } = useStructuredSectionField({
        activeLanguageIndex,
        commentMarkedHtml,
        control,
        index,
    });
    const openThread = useOpenThread(itemId ?? '');

    const handleEditorContainerClick = useCallback(
        (event: MouseEvent<HTMLDivElement>): void => {
            if (!is.string(itemId) || !is.string(sectionKey)) return;
            const target = event.target instanceof HTMLElement ? event.target : null;
            if (is.null(target)) return;
            const mark = target.closest<HTMLElement>(commentMarkSelector());
            if (is.null(mark)) return;
            const commentId = mark.getAttribute(COMMENT_ID_ATTRIBUTE);
            if (is.null(commentId)) return;
            openThread(commentId, { kind: 'description-section', sectionId: sectionKey });
        },
        [openThread, itemId, sectionKey],
    );

    return (
        <StructuredSectionEditor
            activeLanguageIndex={activeLanguageIndex}
            editorValue={editorValue}
            index={index}
            isGenerating={isGenerating}
            itemId={itemId}
            sectionKey={sectionKey}
            sectionTitle={sectionTitle}
            onChange={handleEditorChange}
            onContainerClick={handleEditorContainerClick}
            onUserInput={onUserInput}
        >
            {is.string(sectionKey) ? (
                <>
                    <CommentSelectionPlugin itemId={itemId ?? ''} sectionId={sectionKey} />
                    <CommentMarkReconcilePluginFromComments editorValue={editorValue} sectionKey={sectionKey} />
                </>
            ) : null}
        </StructuredSectionEditor>
    );
};

export const EditableDescriptionSection = memo((props: EditableDescriptionSectionProps) =>
    props.commentable ? (
        <CommentableEditableDescriptionSection {...props} />
    ) : (
        <PlainEditableDescriptionSection {...props} />
    ),
);
EditableDescriptionSection.displayName = 'EditableDescriptionSection';

export const DescriptionSection: FunctionComponent<{
    readonly commentable: boolean;
    readonly itemId?: string;
    readonly sectionKey?: string;
    readonly title: string;
    readonly value: string;
}> = ({ commentable, itemId, sectionKey, title, value }) => {
    if (!commentable || is.undefined(itemId) || is.undefined(sectionKey)) {
        return <PlainDescriptionSection html={value} sectionTitle={title} />;
    }
    return <CommentableDescriptionSection html={value} itemId={itemId} sectionKey={sectionKey} sectionTitle={title} />;
};
